import { Avatar, Button, Card, Field, Input, Switch, useToast } from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';

import { api, ApiError } from '../api/client.js';
import type { MeResponse } from '../api/types.js';

/** Bekannte Benachrichtigungstypen — pro Nutzer an-/abschaltbar. */
const NOTIFICATION_TYPES = [
  { key: 'projects.task.assigned', label: 'Aufgabe zugewiesen' },
  { key: 'projects.task.commented', label: 'Neuer Kommentar an meiner Aufgabe' },
  { key: 'projects.task.mentioned', label: 'In einem Kommentar erwähnt' },
  { key: 'crm.deal.won', label: 'Deal gewonnen' },
  { key: 'sales.quote.accepted', label: 'Angebot angenommen' },
  { key: 'accounting.invoice.paid', label: 'Rechnung bezahlt' },
] as const;

const NOTIFY_PREFS = 'core.notification_prefs';

/** Eigenes Profil: Anzeigename + Passwort ändern. */
export default function ProfilePage() {
  const { me } = useOutletContext<{ me: MeResponse }>();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [displayName, setDisplayName] = useState(me.user.displayName);
  const [passwords, setPasswords] = useState({ current: '', next: '', repeat: '' });

  const saveProfile = useMutation({
    mutationFn: () =>
      api('/api/v1/me/profile', { method: 'PUT', body: { displayName }, tenant: false }),
    onSuccess: () => {
      toast.success('Profil gespeichert');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const changePassword = useMutation({
    mutationFn: () =>
      api('/api/v1/me/password', {
        method: 'PUT',
        body: { currentPassword: passwords.current, newPassword: passwords.next },
        tenant: false,
      }),
    onSuccess: () => {
      setPasswords({ current: '', next: '', repeat: '' });
      toast.success('Passwort geändert');
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Passwort-Änderung fehlgeschlagen');
    },
  });

  const passwordMismatch = passwords.repeat.length > 0 && passwords.next !== passwords.repeat;

  // Benachrichtigungs-Einstellungen (view_prefs: {disabled: string[]})
  const [disabled, setDisabled] = useState<string[]>([]);
  const notifyPrefs = useQuery({
    queryKey: ['view-prefs', NOTIFY_PREFS],
    queryFn: () =>
      api<{ config: { disabled?: string[] } | null }>(
        `/api/v1/view-prefs?entityType=${NOTIFY_PREFS}`,
      ),
  });
  useEffect(() => {
    const stored = notifyPrefs.data?.config?.disabled;
    if (Array.isArray(stored)) setDisabled(stored);
  }, [notifyPrefs.data]);

  const saveNotifyPrefs = useMutation({
    mutationFn: (next: string[]) =>
      api('/api/v1/view-prefs', {
        method: 'PUT',
        body: { entityType: NOTIFY_PREFS, config: { disabled: next } },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['view-prefs', NOTIFY_PREFS] }),
    onError: (error) => toast.error(error.message),
  });

  const toggleNotify = (key: string, enabled: boolean): void => {
    const next = enabled ? disabled.filter((entry) => entry !== key) : [...disabled, key];
    setDisabled(next);
    saveNotifyPrefs.mutate(next);
  };

  return (
    <div className="page">
      <h1 className="page__title">Mein Profil</h1>

      <Card>
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--au-space-3)' }}
          onSubmit={(event) => {
            event.preventDefault();
            saveProfile.mutate();
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--au-space-3)' }}>
            <Avatar name={displayName || me.user.displayName} size={48} />
            <div>
              <div style={{ fontWeight: 600 }}>{me.user.email}</div>
              <div style={{ fontSize: 'var(--au-text-xs)', color: 'var(--au-text-muted)' }}>
                Avatar entsteht aus deinem Namen
              </div>
            </div>
          </div>
          <Field label="Anzeigename">
            {(id) => (
              <Input
                id={id}
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            )}
          </Field>
          <div>
            <Button type="submit" loading={saveProfile.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--au-space-3)' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!passwordMismatch && passwords.next.length >= 10) changePassword.mutate();
          }}
        >
          <h2 style={{ fontSize: 'var(--au-text-lg)' }}>Passwort ändern</h2>
          <Field label="Aktuelles Passwort">
            {(id) => (
              <Input
                id={id}
                type="password"
                required
                autoComplete="current-password"
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
              />
            )}
          </Field>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--au-space-3)' }}
          >
            <Field label="Neues Passwort" hint="mindestens 10 Zeichen">
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  required
                  minLength={10}
                  autoComplete="new-password"
                  value={passwords.next}
                  onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                />
              )}
            </Field>
            <Field
              label="Wiederholen"
              {...(passwordMismatch ? { error: 'Passwörter stimmen nicht überein.' } : {})}
            >
              {(id, invalid) => (
                <Input
                  id={id}
                  type="password"
                  required
                  aria-invalid={invalid}
                  autoComplete="new-password"
                  value={passwords.repeat}
                  onChange={(e) => setPasswords({ ...passwords, repeat: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div>
            <Button type="submit" loading={changePassword.isPending} disabled={passwordMismatch}>
              Passwort ändern
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Benachrichtigungen">
        <p
          style={{
            color: 'var(--au-text-muted)',
            fontSize: 'var(--au-text-sm)',
            marginBottom: 'var(--au-space-3)',
          }}
        >
          Welche Ereignisse sollen in der Glocke erscheinen?
        </p>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {NOTIFICATION_TYPES.map((type) => (
            <li
              key={type.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--au-space-3)',
                padding: 'var(--au-space-2) 0',
                borderTop: '1px solid var(--au-border)',
              }}
            >
              <span>{type.label}</span>
              <Switch
                checked={!disabled.includes(type.key)}
                onChange={(enabled) => toggleNotify(type.key, enabled)}
                aria-label={`Benachrichtigung „${type.label}"`}
              />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
